import { addonKindsOf, modLoaderOf } from '@mcpanel/core';
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { BuildInfo, SoftwareFamily, SoftwareInfo, SoftwareSpec, VersionInfo } from '../api/types';
import { errorMessage } from '../app-context';

/** ソフト・MC バージョン・ビルドを API から選ぶ。 */
export function SoftwarePicker({
  value,
  onChange,
  lockFamily = false,
  currentSpec,
}: {
  value: SoftwareSpec;
  onChange: (spec: SoftwareSpec) => void;
  lockFamily?: boolean;
  currentSpec?: SoftwareSpec;
}) {
  const [software, setSoftware] = useState<SoftwareInfo[]>([]);
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [builds, setBuilds] = useState<BuildInfo[]>([]);
  const [snapshots, setSnapshots] = useState(false);
  const [error, setError] = useState<string>();
  const [checkedList, setCheckedList] = useState('');

  useEffect(() => {
    api
      .software()
      .then(setSoftware)
      .catch((e: unknown) => setError(errorMessage(e)));
  }, []);

  useEffect(() => {
    let active = true;
    setVersions([]);
    setCheckedList('');
    setError(undefined);
    api
      .versions(value.family, snapshots)
      .then((items) => {
        if (!active) return;
        setVersions(items);
        setCheckedList(`${value.family}:${snapshots}`);
      })
      .catch((e: unknown) => {
        if (active) setError(errorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [value.family, snapshots]);

  useEffect(() => {
    if (checkedList !== `${value.family}:${snapshots}` || !value.mcVersion) return;
    if (currentSpec?.family === value.family && currentSpec.mcVersion === value.mcVersion) return;
    if (versions.some((v) => v.id === value.mcVersion && v.availability !== 'unchecked')) return;
    onChange({ family: value.family, mcVersion: '' });
  }, [checkedList, versions, value.family, value.mcVersion, snapshots, currentSpec, onChange]);

  useEffect(() => {
    let active = true;
    setBuilds([]);
    if (!value.mcVersion) return;
    if (!versions.some((v) => v.id === value.mcVersion && v.availability !== 'unchecked')) return;
    api
      .builds(value.family, value.mcVersion)
      .then((items) => {
        if (active) setBuilds(items);
      })
      .catch((e: unknown) => {
        if (active) setError(errorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [value.family, value.mcVersion, versions]);

  const capabilities = addonKindsOf(value.family);
  const usesLoader = capabilities.mods && !capabilities.plugins;
  const buildKey = usesLoader ? 'loaderVersion' : 'build';
  const current = software.find((s) => s.family === value.family);

  const setFamily = (family: SoftwareFamily) => onChange({ family, mcVersion: value.mcVersion });
  const setVersion = (mcVersion: string) => onChange({ family: value.family, mcVersion });
  const setBuild = (build: string) => {
    const next: SoftwareSpec = { family: value.family, mcVersion: value.mcVersion };
    if (build) next[buildKey] = build;
    onChange(next);
  };

  return (
    <div className="stack" style={{ gap: 12 }}>
      {value.family === 'asyncyouer' &&
        checkedList === value.family + ':' + snapshots &&
        versions.length === 0 &&
        !error && <div className="alert info">AsyncYouer は公開されている版がありません（MohistMC の有料版のみ）</div>}
      {error && <div className="alert danger">{error}</div>}
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <div className="field">
          <label htmlFor="sw-family">ソフト</label>
          <select
            id="sw-family"
            className="input"
            value={value.family}
            disabled={lockFamily}
            onChange={(e) => setFamily(e.target.value as SoftwareFamily)}
          >
            {software.map((s) => (
              <option key={s.family} value={s.family} disabled={!s.supported}>
                {s.label}
                {addonKindsOf(s.family).mods && addonKindsOf(s.family).plugins
                  ? '（Mod・プラグイン、' + (modLoaderOf(s.family) === 'forge' ? 'Forge 系' : 'NeoForge 系') + '）'
                  : s.kind === 'plugin'
                    ? '（プラグイン）'
                    : s.kind === 'mod'
                      ? '（Mod）'
                      : ''}
                {s.supported ? '' : ' — 未対応'}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="sw-version">Minecraft バージョン</label>
          <select
            id="sw-version"
            className="input"
            value={value.mcVersion}
            onChange={(e) => setVersion(e.target.value)}
          >
            <option value="">選択してください</option>
            {currentSpec?.family === value.family && !versions.some((v) => v.id === currentSpec.mcVersion) && (
              <option value={currentSpec.mcVersion} disabled>
                {currentSpec.mcVersion}（現在）
              </option>
            )}
            {versions.map((v) => (
              <option key={v.id} value={v.id} disabled={v.availability === 'unchecked'}>
                {v.id}
                {currentSpec?.family === value.family && currentSpec.mcVersion === v.id ? '（現在）' : ''}
                {v.type === 'snapshot' ? '（スナップショット）' : ''}
                {v.availability === 'unchecked' ? `（${v.message ?? '確認できませんでした'}）` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="sw-build">{usesLoader ? 'ローダーのバージョン' : 'ビルド'}</label>
          <select
            id="sw-build"
            className="input"
            value={value[buildKey] ?? ''}
            onChange={(e) => setBuild(e.target.value)}
            disabled={!value.mcVersion || builds.length === 0}
          >
            <option value="">最新（推奨）</option>
            {currentSpec?.family === value.family &&
              currentSpec.mcVersion === value.mcVersion &&
              currentSpec[buildKey] &&
              !builds.some((b) => b.id === currentSpec[buildKey]) && (
                <option value={currentSpec[buildKey]} disabled>
                  {currentSpec[buildKey]}（現在）
                </option>
              )}
            {builds.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label ?? b.id}
                {b.recommended ? '（推奨）' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>
      <label className="check small muted">
        <input type="checkbox" checked={snapshots} onChange={(e) => setSnapshots(e.target.checked)} />
        開発版（スナップショット）も表示する
      </label>
      {current && (
        <p className="small faint" style={{ margin: 0 }}>
          {capabilities.mods && capabilities.plugins
            ? 'Mod と プラグインを一緒に使えるサーバーです（' +
              (modLoaderOf(value.family) === 'forge' ? 'Forge 系' : 'NeoForge 系') +
              '）。'
            : current.kind === 'plugin'
              ? 'プラグイン（Bukkit 系）で拡張するサーバーです。Mod は入れられません。'
              : current.kind === 'mod'
                ? 'Mod を入れられるサーバーです。参加するプレイヤーも同じ Mod が必要です。'
                : '公式のサーバーです。プラグインも Mod も入れられません。'}
        </p>
      )}
    </div>
  );
}
